import type { GridSortDirection } from '@mui/x-data-grid-pro';
import is from '@sindresorhus/is';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import { z } from 'zod';

import { ApplicationContext } from '~/shared/context/context';
import type { SortedField } from '~/shared/data-contract/common';
import { paginationSchema } from '~/shared/data-contract/pagination';
import type { RelatedJobsOptions, RelatedSkills, SkillDetails } from '~/shared/data-contract/skill-details';
import { relatedJobSchema, relatedSkillsSchema, skillDetailsSchema } from '~/shared/data-contract/skill-details';
import type { Pathname } from '~/shared/http-fetcher/http-fetcher';
import { useUserAmbitionsQuery } from '~/shared/query-hooks/use-ambition';

const getSortSign = (sort: GridSortDirection): string => (sort === 'desc' ? '-' : '');

const getSortParams = (sortedFields: readonly SortedField[]): string =>
    sortedFields.map((curItem) => `${getSortSign(curItem.direction)}${curItem.field}`, []).join(',');

export const useSkillDetailsQuery = (skillId: string | null = null): UseQueryResult<SkillDetails, Error> => {
    const { applicationState, validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const { language } = applicationState;

    const pathname: Pathname = `/skills/${skillId}/details`;

    return useQuery<SkillDetails>({
        enabled: is.nonEmptyString(skillId),
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(skillDetailsSchema, {
                pathname,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, 'GET', language.code],
    });
};

export const useRelatedSkillsQuery = (skillId: string | null = null): UseQueryResult<RelatedSkills, Error> => {
    const { applicationState, validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const { language } = applicationState;

    const pathname: Pathname = `/skills/${skillId}/related`;

    return useQuery<RelatedSkills>({
        enabled: is.nonEmptyString(skillId),
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(relatedSkillsSchema, {
                pathname,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, 'GET', language.code],
    });
};

export const useSkillDetailWithIsAmbitionQuery = (
    userId: string,
    skillId?: string,
): UseQueryResult<
    SkillDetails & {
        readonly isAmbition?: boolean;
    }
> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    const { data: ambitions } = useUserAmbitionsQuery(userId);

    const isAmbition = ambitions?.some((curItem) => curItem.id === skillId);
    const pathname: `/${string}` = `/skills/${skillId}/details`;

    return useQuery({
        enabled: Boolean(skillId) && Boolean(userId),
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(skillDetailsSchema, {
                pathname,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, 'GET'],
        select: (data) => ({
            ...data,
            isAmbition,
        }),
    });
};

export const skillRelatedJobsPaginationSchema = z.object({
    data: relatedJobSchema.array(),
    pagination: paginationSchema,
});

type SkillRelatedJobs = z.infer<typeof skillRelatedJobsPaginationSchema>;

export const useSkillRelatedJobsQuery = (
    skillId: string | null = null,
    options: RelatedJobsOptions,
    sortOptions?: readonly SortedField[],
): UseQueryResult<SkillRelatedJobs> => {
    const { applicationState, validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const { language } = applicationState;
    const pathname: `/${string}` = `/skills/${skillId}/jobs`;

    const searchParams = is.array(sortOptions)
        ? {
              ...options,
              sort: getSortParams(sortOptions),
          }
        : options;

    return useQuery({
        enabled: Boolean(skillId),
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(skillRelatedJobsPaginationSchema, {
                pathname,
                requestOptions: {
                    searchParams,
                },
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, 'GET', searchParams, language.code],
    });
};
